import React from 'react';
import TrainingLeadsPage from './pages/modules/crm/TrainingLeadsPage';
import FullStackCrashCourse from './pages/public/FullStackCrashCourse';
import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TenantProvider } from './context/TenantContext';
import { DemoProvider } from './context/DemoContext';
import { VersionProvider } from './context/VersionContext';

// Layouts
import SuperAdminLayout from './layouts/SuperAdminLayout';
import TenantLayout from './layouts/TenantLayout';
import AuthLayout from './layouts/AuthLayout';

// Auth Pages
import Login from './pages/auth/Login';
import WebsiteBlogAuthorize from './pages/auth/WebsiteBlogAuthorize';
import Register from './pages/auth/Register';
import UserRegister from './pages/auth/UserRegister';
import TenantUserRegister from './pages/auth/TenantUserRegister';
import SALogin from './pages/superadmin/SALogin';

// Super Admin Pages
import SADashboard from './pages/superadmin/SADashboard';
import SAIndustries from './pages/superadmin/SAIndustries';
import SAModules from './pages/superadmin/SAModules';
import SAPlans from './pages/superadmin/SAPlans';
import SATenants from './pages/superadmin/SATenants';
import SANotifications from './pages/superadmin/SANotifications';
import SAMigrate from './pages/superadmin/SAMigrate';
import SAAppVersion from './pages/superadmin/SAAppVersion';

// Tenant Pages
import TenantDashboard from './pages/tenant/TenantDashboard';
import Users from './pages/tenant/Users';
import Roles from './pages/tenant/Roles';
import Settings from './pages/tenant/Settings';
import Subscription from './pages/tenant/Subscription';
import Profile from './pages/tenant/StudentProfilePage';
import DeleteAccountPage from './pages/tenant/DeleteAccountPage';

// Module Pages
import DynamicModulePage from './pages/modules/DynamicModulePage';
import StudentsPage from './pages/modules/education/StudentsPage';
import AttendancePage from './pages/modules/education/AttendancePage';
import ExamsPage from './pages/modules/education/ExamsPage';
import FeesPage from './pages/modules/education/FeesPage';
import AdmissionManagement from './pages/modules/education/AdmissionManagement';
import SchoolConfigPage from './pages/modules/education/SchoolConfigPage';
import TimetablePage from './pages/modules/education/TimetablePage';
import AdvancedCourseContent from './pages/modules/education/AdvancedCourseContent';
import CourseNotesPage from './pages/modules/education/CourseNotesPage';
import StudentCoursePage from './pages/modules/education/StudentCoursePage';
import QuestionPaperGenerator from './pages/modules/education/QuestionPaperGenerator';
import SyllabusTrackingPage from './pages/modules/education/SyllabusTrackingPage';
import RankingSystemPage from './pages/modules/education/RankingSystemPage';
import StaffTimetablePage from './pages/modules/education/StaffTimetablePage';
import EducationCourseContentPage from './pages/modules/education/EducationCourseContentPage';
import TransportPage from './pages/modules/education/TransportPage';
import StudentPerformancePage from './pages/modules/education/StudentPerformancePage';
import PaidStudentProgressPage from './components/progress/PaidStudentProgressPage';
import ManagementCommunication from './pages/modules/communication/ManagementCommunication';
import StudentCommunication from './pages/modules/communication/StudentCommunication';
import QuizGenerator from './pages/modules/quiz/QuizGenerator';
import TrainingTimetablePage from './pages/modules/training/TimetablePage';
import Courses from './pages/modules/training/Courses';
import OnDemandCoursesPage from './pages/modules/training/OnDemandCoursesPage';
import Batches from './pages/modules/training/Batches';
import Enrollments from './pages/modules/training/Enrollments';
import TrainingAttendance from './pages/modules/training/Attendance';
import Assessments from './pages/modules/training/Assessments';
import Enquiries from './pages/modules/training/Enquiries';
import WorkAttendance from './pages/modules/training/WorkAttendance';
import DailyReports from './pages/modules/training/DailyReports';
import Announcements from './pages/modules/training/Announcements';
import CourseContentPage from './pages/modules/training/CourseContentPage';
import CourseRecordsPage from './pages/modules/training/CourseRecordsPage';
import FeedbackPage from './pages/modules/training/FeedbackPage';
import ResumesPage from './pages/modules/training/ResumesPageV2';
import StudentJobOpportunitiesPage from './pages/modules/training/StudentJobOpportunitiesPage';
import ChatPage from './pages/modules/training/ChatPage';
import ContentGeneratorPage from './pages/modules/training/ContentGeneratorPage';
import HRMPage from './pages/modules/training/HRMPage';
import DataMigration from './pages/modules/training/DataMigration';
import CertificatesPage from './pages/modules/training/CertificatesPage';
import PromotionsPage from './pages/modules/training/PromotionsPage';
import { InternshipsPage, ProjectsPage } from './pages/modules/training/InternshipProjectPage';
import VerifyCertificate from './pages/public/VerifyCertificate';
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import EmployeesPage from './pages/modules/hrm/EmployeesPage';
import LeavePage from './pages/modules/hrm/LeavePage';
import LeaveApprovalPage from './pages/modules/hrm/LeaveApprovalPage';
import RequestApprovalPage from './pages/common/RequestApprovalPage';
import TasksPage from './pages/modules/shared/TasksPage';
import PayrollPage from './pages/modules/hrm/PayrollPage';
import NotificationsPage from './pages/modules/shared/NotificationsPage';
import HtmlEditorPage from './pages/HtmlEditorPage';
import { isCollegeStudentExperience, studentHomePath } from './utils/studentExperience';

const normalizedRoleKey = (user) => String(user?.roleKey || user?.role || '').trim().toLowerCase();

const defaultTenantHome = (user) => {
  const roleKey = normalizedRoleKey(user);
  return roleKey === 'student' ? studentHomePath(user) : '/dashboard';
};

// Route Guards
const ProtectedRoute = ({ children, superAdminOnly = false }) => {
  const { user, loading, isSuperAdmin } = useAuth();
  if (loading) return <div className="page-loader"><div className="spinner-border text-primary" role="status"></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (superAdminOnly && !isSuperAdmin) return <Navigate to={defaultTenantHome(user)} replace />;
  return children;
};

// AdminOnlyRoute: Users / Roles / Settings pages are only for super_admin, admin
// or manager. Any other role hitting /users, /roles, /settings directly bounces
// back to /dashboard. The sidebar already hides these — this is the matching
// route-level guard.
const ELEVATED_ROLE_KEYS_ROUTE = new Set(['super_admin', 'admin', 'manager']);
const AdminOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="page-loader"><div className="spinner-border text-primary" role="status"></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  const rk = normalizedRoleKey(user);
  if (!ELEVATED_ROLE_KEYS_ROUTE.has(rk)) return <Navigate to={defaultTenantHome(user)} replace />;
  return children;
};

// AdminStrictRoute: stricter than AdminOnlyRoute — only `super_admin` or
// `admin` may access. Used for billing surfaces (Subscription) that should
// not be visible to manager / team_lead either.
const AdminStrictRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="page-loader"><div className="spinner-border text-primary" role="status"></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  const rk = normalizedRoleKey(user);
  const isStrictAdmin = rk === 'admin' || rk === 'super_admin' || user.userType === 'super_admin';
  if (!isStrictAdmin) return <Navigate to={defaultTenantHome(user)} replace />;
  return children;
};

const NotStudentRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="page-loader"><div className="spinner-border text-primary" role="status"></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  const rk = normalizedRoleKey(user);
  if (rk === 'student') return <Navigate to={defaultTenantHome(user)} replace />;
  return children;
};

const ExcludeLegacyPaidStudentRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="page-loader"><div className="spinner-border text-primary" role="status"></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  const isLegacyPaidStudent = normalizedRoleKey(user) === 'student'
    && !isCollegeStudentExperience(user);
  if (isLegacyPaidStudent) return <Navigate to={defaultTenantHome(user)} replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading, isSuperAdmin, logout } = useAuth();
  const [params] = useSearchParams();
  const location = useLocation();
  const tenantParam = params.get('tenant');
  const pathTenantCandidate = String(location.pathname || '').replace(/^\/+|\/+$/g, '');
  const tenantScopedCollegeLanding = pathTenantCandidate && !pathTenantCandidate.includes('/');

  if (loading) return null;

  // If a tenant-scoped URL is being accessed (e.g. /login?tenant=xxx or /register/user?tenant=xxx),
  // allow access even when another user is logged in — and clear the stale session first so the
  // tenant login/register page renders instead of bouncing back to the previous dashboard.
  if (user && (tenantParam || tenantScopedCollegeLanding)) {
    logout();
    return children;
  }

  if (user) return <Navigate to={isSuperAdmin ? '/sa/dashboard' : defaultTenantHome(user)} replace />;
  return children;
};

// ModuleRoute: only renders the route if the module is in the tenant's activeModules list
const ModuleRoute = ({ moduleKey, children }) => {
  const { activeModules, user, loading } = useAuth();
  if (loading) return <div className="page-loader"><div className="spinner-border text-primary" role="status"></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  // Super admin and core routes always pass through
  if (user.userType === 'super_admin') return children;
  if (!moduleKey) return children;
  const roleKey = normalizedRoleKey(user);
  // Student-facing destinations can be present in a student's dedicated
  // navigation even when an older activeModules payload lacks the newer key.
  const studentFacingModules = new Set(['resumes', 'student_course', 'student_performance', 'course-content']);
  if (roleKey === 'student' && studentFacingModules.has(moduleKey)) return children;
  const allowed = activeModules.some(m => m.key === moduleKey);
  if (!allowed) return <Navigate to={defaultTenantHome(user)} replace />;
  return children;
};

const TenantDashboardEntry = () => {
  const { user } = useAuth();
  if (normalizedRoleKey(user) === 'student' && isCollegeStudentExperience(user)) {
    return <Navigate to="/lms?tab=dashboard" replace />;
  }
  return <TenantDashboard />;
};

const StudentPerformanceEntry = () => {
  const { user } = useAuth();
  const isLegacyPaidStudent = normalizedRoleKey(user) === 'student'
    && !isCollegeStudentExperience(user);
  return isLegacyPaidStudent ? <PaidStudentProgressPage /> : <StudentPerformancePage />;
};

// Route map: moduleKey -> component (specific pages override DynamicModulePage)
const MODULE_PAGES = {
  students: StudentsPage,
  student_management: StudentsPage,
  attendance: AttendancePage,
  exams: ExamsPage,
  exam_assessment: ExamsPage,
  fees: FeesPage,
  fee_management: FeesPage,
  school_config: SchoolConfigPage,
  // Timetable for education tenants now defaults to the structured grid view
  // (class teacher / substitute / PET per period). Training tenants get their
  // own page via the explicit /timetable route below.
  timetable: StaffTimetablePage,
  question_paper_generator: QuestionPaperGenerator,
  syllabus_tracking: SyllabusTrackingPage,
  ranking_system: RankingSystemPage,
  staff_timetable: StaffTimetablePage,
  transport: TransportPage,
  student_performance: StudentPerformancePage,
  // Education tenants get the rich course-tree editor (modules → topics → blocks,
  // with reuse + AI + Word import). The legacy AdvancedCourseContent /
  // CourseContentPage components are kept available below for routes that
  // explicitly target them (e.g. training-tenant flows).
  advanced_course_content: EducationCourseContentPage,
  course_content: EducationCourseContentPage,
  admission_management: AdmissionManagement,
  admissions: AdmissionManagement,
  course_notes: CourseNotesPage,
  student_course: StudentCoursePage,
  content: StudentCoursePage,
  course_content_notes: CourseNotesPage,
  management_communication: ManagementCommunication,
  student_communication: StudentCommunication,
  hrm: EmployeesPage,
  leave: LeavePage,
  leave_management: LeavePage,
  tasks: TasksPage,
  payroll: PayrollPage,
  notifications: NotificationsPage,
};

// Used for explicit named routes (moduleKey passed as prop)
const DynamicRoute = ({ moduleKey }) => {
  const Component = MODULE_PAGES[moduleKey] || DynamicModulePage;
  return <Component moduleKey={moduleKey} />;
};

// Defensive industry-slug extraction. `tenant.industryId` may be populated
// (object with .slug) or raw (id string), and `tenant.industry` could be a
// string or object depending on how the tenant was loaded. Always coerce to
// a string before toLowerCase so we never crash on an object ref.
const tenantIndustrySlug = (tenant) => {
  if (!tenant) return '';
  const refs = [tenant.industryId, tenant.industry, tenant.industrySlug];
  for (const ref of refs) {
    if (typeof ref === 'string' && ref) return ref.toLowerCase();
    if (ref && typeof ref === 'object') {
      if (typeof ref.slug === 'string' && ref.slug) return ref.slug.toLowerCase();
      if (typeof ref.name === 'string' && ref.name) return ref.name.toLowerCase();
    }
  }
  return '';
};
const isEducationTenantSlug = (slug) => slug === 'education' || slug === 'school';

// Picks the right Timetable page based on tenant industry. Education tenants
// get the structured grid (class teacher / substitute / PET); training tenants
// keep their existing per-row page.
const TimetableForTenant = () => {
  const { tenant } = useAuth();
  return isEducationTenantSlug(tenantIndustrySlug(tenant))
    ? <StaffTimetablePage /> : <TrainingTimetablePage />;
};

// Same idea for /attendance — education tenants get the class-based attendance
// page (which uses class+section, not batch), training tenants get their
// existing batch-based page.
const AttendanceForTenant = () => {
  const { tenant } = useAuth();
  return isEducationTenantSlug(tenantIndustrySlug(tenant))
    ? <AttendancePage /> : <TrainingAttendance />;
};

// Catch-all: reads :moduleKey from URL, normalises hyphens→underscores, checks MODULE_PAGES
const DynamicCatchAll = () => {
  const { moduleKey } = useParams();
  const normalized = (moduleKey || '').replace(/-/g, '_');
  const Component = MODULE_PAGES[normalized] || MODULE_PAGES[moduleKey] || DynamicModulePage;
  return <Component moduleKey={normalized || moduleKey} />;
};

function AppRoutes() {
  const { activeModules } = useAuth();

  return (
    <Routes>
      {/* Public */}
      <Route path="/full-stack-crash-course-in-puducherry" element={<FullStackCrashCourse />} />
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/verify/:token" element={<VerifyCertificate />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
      <Route path="/website-blog/authorize" element={<WebsiteBlogAuthorize />} />
      <Route path="/login" element={<PublicRoute><AuthLayout><Login /></AuthLayout></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><AuthLayout><Register /></AuthLayout></PublicRoute>} />
      <Route path="/register/user" element={<PublicRoute><AuthLayout><TenantUserRegister /></AuthLayout></PublicRoute>} />
      <Route path="/:collegeSlug" element={<PublicRoute><AuthLayout><UserRegister /></AuthLayout></PublicRoute>} />
      <Route path="/sa/login" element={<PublicRoute><AuthLayout sa><SALogin /></AuthLayout></PublicRoute>} />

      {/* Email-based approval pages — public, no login required.
          The signed HMAC token in the URL is the auth. */}
      <Route path="request-approval/:action/:token" element={<RequestApprovalPage />} />
      <Route path="attendance/request/:action/:token" element={<RequestApprovalPage />} />

      {/* Super Admin */}
      <Route path="/sa" element={<ProtectedRoute superAdminOnly><SuperAdminLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/sa/dashboard" replace />} />
        <Route path="dashboard" element={<SADashboard />} />
        <Route path="tenants" element={<SATenants />} />
        <Route path="industries" element={<SAIndustries />} />
        <Route path="modules" element={<SAModules />} />
        <Route path="plans" element={<SAPlans />} />
        <Route path="notifications" element={<SANotifications />} />
        <Route path="migrate" element={<SAMigrate />} />
        <Route path="app-version" element={<SAAppVersion />} />
      </Route>

      {/* Tenant */}
      <Route path="/" element={<ProtectedRoute><TenantLayout /></ProtectedRoute>}>
        <Route path="dashboard" element={<TenantDashboardEntry />} />
        <Route path="crm" element={<TrainingLeadsPage />} />
        <Route path="users" element={<AdminOnlyRoute><Users /></AdminOnlyRoute>} />
        <Route path="roles" element={<AdminOnlyRoute><Roles /></AdminOnlyRoute>} />
        <Route path="settings" element={<AdminOnlyRoute><Settings /></AdminOnlyRoute>} />
        <Route path="subscription" element={<AdminStrictRoute><Subscription /></AdminStrictRoute>} />
        <Route path="profile" element={<Profile />} />
        <Route path="delete-account" element={<DeleteAccountPage />} />

        {/* Named module routes — guarded by ModuleRoute (redirects if not in tenant's activeModules) */}
        {/* Education modules */}
        <Route path="students" element={<ModuleRoute moduleKey="student_management"><StudentsPage moduleKey="student_management" /></ModuleRoute>} />
        <Route path="exams" element={<ModuleRoute moduleKey="exam_management"><ExamsPage moduleKey="exam_management" /></ModuleRoute>} />
        <Route path="fees" element={<ModuleRoute moduleKey="fee_management"><FeesPage moduleKey="fee_management" /></ModuleRoute>} />
        <Route path="admissions" element={<ModuleRoute moduleKey="admission_management"><AdmissionManagement /></ModuleRoute>} />
        <Route path="school-config" element={<ModuleRoute moduleKey="school_config"><SchoolConfigPage /></ModuleRoute>} />
        <Route path="course-content-lms" element={<ModuleRoute moduleKey="advanced_course_content"><EducationCourseContentPage /></ModuleRoute>} />
        <Route path="question-papers" element={<ModuleRoute moduleKey="question_paper_generator"><QuestionPaperGenerator /></ModuleRoute>} />
        <Route path="syllabus" element={<ModuleRoute moduleKey="syllabus_tracking"><SyllabusTrackingPage /></ModuleRoute>} />
        <Route path="labs" element={<ModuleRoute moduleKey="lab_management"><DynamicModulePage moduleKey="lab_management" /></ModuleRoute>} />
        <Route path="rankings" element={<ModuleRoute moduleKey="ranking_system"><RankingSystemPage /></ModuleRoute>} />
        <Route path="staff-timetable" element={<ModuleRoute moduleKey="staff_timetable"><StaffTimetablePage /></ModuleRoute>} />
        <Route path="lms" element={<ExcludeLegacyPaidStudentRoute><OnDemandCoursesPage /></ExcludeLegacyPaidStudentRoute>} />
        <Route path="performance" element={<ModuleRoute moduleKey="student_performance"><StudentPerformanceEntry /></ModuleRoute>} />
        <Route path="parent-comm" element={<ModuleRoute moduleKey="parent_communication"><DynamicModulePage moduleKey="parent_communication" /></ModuleRoute>} />
        <Route path="library" element={<ModuleRoute moduleKey="library"><DynamicModulePage moduleKey="library" /></ModuleRoute>} />
        <Route path="transport" element={<ModuleRoute moduleKey="transport"><TransportPage /></ModuleRoute>} />
        <Route path="student-course" element={<ModuleRoute moduleKey="student_course"><StudentCoursePage /></ModuleRoute>} />
        {/* Training modules */}
        <Route path="courses" element={<ModuleRoute moduleKey="courses"><Courses /></ModuleRoute>} />
        <Route path="batches" element={<ModuleRoute moduleKey="batches"><Batches /></ModuleRoute>} />
        <Route path="enrollments" element={<ModuleRoute moduleKey="enrollments"><Enrollments /></ModuleRoute>} />
        <Route path="enquiries" element={<ModuleRoute moduleKey="enquiries"><Enquiries /></ModuleRoute>} />
        <Route path="internships" element={<ModuleRoute moduleKey="internships"><InternshipsPage /></ModuleRoute>} />
        <Route path="projects" element={<ModuleRoute moduleKey="projects"><ProjectsPage /></ModuleRoute>} />
        <Route path="course-records" element={<ModuleRoute moduleKey="course-records"><CourseRecordsPage /></ModuleRoute>} />
        <Route path="resumes" element={<ModuleRoute moduleKey="resumes"><ResumesPage /></ModuleRoute>} />
        <Route path="job-opportunities" element={<ModuleRoute moduleKey="resumes"><StudentJobOpportunitiesPage /></ModuleRoute>} />
        <Route path="quiz-generator" element={<ModuleRoute moduleKey="quiz-generator"><QuizGenerator /></ModuleRoute>} />
        <Route path="content-generator" element={<ModuleRoute moduleKey="content-generator"><ContentGeneratorPage /></ModuleRoute>} />
        <Route path="course-content" element={<ModuleRoute moduleKey="course-content"><CourseContentPage /></ModuleRoute>} />
        <Route path="course-notes" element={<DynamicRoute moduleKey="course_notes" />} />
        {/* Shared routes — available to all tenants */}
        <Route path="attendance" element={<ExcludeLegacyPaidStudentRoute><AttendanceForTenant /></ExcludeLegacyPaidStudentRoute>} />
        <Route path="timetable" element={<NotStudentRoute><TimetableForTenant /></NotStudentRoute>} />
        <Route path="assessments" element={<Assessments />} />
        <Route path="work-attendance" element={<WorkAttendance />} />
        <Route path="daily-reports" element={<DailyReports />} />
        <Route path="hrm" element={<HRMPage moduleKey="hrm" />} />
        <Route path="leave" element={<LeavePage moduleKey="leave_management" />} />
        <Route path="leave/:id" element={<LeavePage moduleKey="leave_management" />} />
        <Route path="leave/approval/approve/:token" element={<LeaveApprovalPage />} />
        <Route path="leave/approval/reject/:token" element={<LeaveApprovalPage />} />
        <Route path="tasks" element={<NotStudentRoute><TasksPage moduleKey="tasks" /></NotStudentRoute>} />
        <Route path="payroll" element={<PayrollPage moduleKey="payroll" />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="announcements" element={<Announcements />} />
        <Route path="feedback" element={<FeedbackPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="mgmt-communication" element={<ManagementCommunication />} />
        <Route path="student-communication" element={<StudentCommunication />} />
        <Route path="documents" element={<DynamicModulePage moduleKey="documents" />} />
        <Route path="reports" element={<DynamicModulePage moduleKey="reports" />} />
        <Route path="sports" element={<DynamicModulePage moduleKey="sports" />} />
        <Route path="competitions" element={<DynamicModulePage moduleKey="competitions" />} />
        <Route path="certificates" element={<CertificatesPage />} />
        <Route path="student-showcase" element={<PromotionsPage />} />
        <Route path="inventory" element={<DynamicModulePage moduleKey="inventory_management" />} />

        {/* Hidden Confidential Routes - accessible only via direct URL */}
        <Route path="dm" element={<DataMigration />} />

        {/* Dynamic catch-all for any other module */}
        <Route path=":moduleKey" element={<DynamicCatchAll />} />
      </Route>

      <Route path="/html-editor" element={<ProtectedRoute><HtmlEditorPage /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <DemoProvider>
          <TenantProvider>
            <VersionProvider>
              <AppRoutes />
            </VersionProvider>
          </TenantProvider>
        </DemoProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
